/* eslint-disable import/order */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";

import { editCustomer as editCustomerAPI } from "api/customers";
import { customersKeys } from "api/customers/queries";
import { CustomersType, EditCustomerType } from "api/customers/types";
import AppDialog from "components/AppDialog";
import ModalHeader from "components/AppDialog/ModalHeader";
import { generateCustomerValues } from "utils/generateCustomerValues/generateCustomerValues";

import CustomerForm, { CustomerFormValues } from "./CustomerForm";

interface EditCustomerModalProps {
  isOpen: boolean;
  handleClose: () => void;
  customer: CustomersType | undefined;
}

const EditCustomerModal = ({
  isOpen,
  handleClose,
  customer,
}: EditCustomerModalProps) => {
  const queryClient = useQueryClient();

  const initialValues: CustomerFormValues = generateCustomerValues(customer);

  const { mutateAsync: editCustomer, status } = useMutation(
    async (data: EditCustomerType) => {
      const res = await editCustomerAPI(data);
      await queryClient.invalidateQueries(customersKeys.details(data._id));
      return res.data;
    },
  );

  const handleSubmit = async (values: CustomerFormValues) => {
    const mappedValues: EditCustomerType = {
      _id: customer!._id,
      ...values,
    };
    await editCustomer(mappedValues, {
      onSuccess: () => handleClose(),
    });
  };

  return (
    <AppDialog open={isOpen} onClose={handleClose} maxWidth="lg">
      <ModalHeader
        title="Edit Customer"
        subtitle="Update customer information and contact details."
      />

      <CustomerForm
        edit
        submitStatus={status}
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        handleClose={handleClose}
        customerId={customer?._id}
      />
    </AppDialog>
  );
};

export default EditCustomerModal;
